import React, { useRef, useState } from 'react';
import {
    uploadImage,
    validateImageFile,
    type ImageUploadResult,
} from '@/features/dashboard/channels/chat/components/shared/attachements/imageUploadUtils';
import { TokenManager } from '@/shared/lib/apiClient';

/** Appends attachment line to the description for DynamoDB persistence. */
export function appendAttachmentSuffixToDescription(
    current: string,
    fileName: string,
    s3FullPath: string
): string {
    const trimmed = current.trimEnd();
    const spacer = trimmed.length > 0 ? '\n\n' : '';
    const line = `Attachement : ${fileName} ( S3 Full Path : ${s3FullPath} )`;
    return trimmed + spacer + line;
}

/** Object key when present; otherwise path segment from HTTPS S3 URL. */
function resolveObjectKeyOrPath(result: ImageUploadResult): string {
    const key = result.key?.trim();
    if (key) return key;
    const url = result.url?.trim() || '';
    if (url.startsWith('https://') && url.includes('.amazonaws.com/')) {
        try {
            const u = new URL(url);
            return decodeURIComponent(u.pathname.replace(/^\//, ''));
        } catch {
            return url;
        }
    }
    return url;
}

/** Prefer full HTTPS URL for display/storage; fall back to key or data URL. */
function resolveS3FullPathForDescription(result: ImageUploadResult): string {
    const url = result.url?.trim() || '';
    if (url.startsWith('https://') || url.startsWith('http://')) {
        return url;
    }
    return resolveObjectKeyOrPath(result) || url;
}

export interface DescriptionAttachmentUploadProps {
    /** When false, nothing is rendered (e.g. view-only description). */
    enabled: boolean;
    projectId?: string | number;
    /** Receives display file name and full S3 URL (or fallback path) to store in description text. */
    onAppend: (fileName: string, s3FullPath: string) => void;
    /** Optional label override */
    label?: string;
}

/**
 * Image upload for requirement descriptions using the same S3 path as chat (imageUploadUtils).
 * Only shown when the parent is in an editable state.
 */
const DescriptionAttachmentUpload: React.FC<DescriptionAttachmentUploadProps> = ({
    enabled,
    projectId,
    onAppend,
    label = 'Add image attachment to description',
}) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [uploading, setUploading] = useState(false);

    if (!enabled) return null;

    const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files?.length) return;

        const idToken = TokenManager.getIdToken();
        const userId = TokenManager.getUserId();

        setUploading(true);
        try {
            for (const file of Array.from(files)) {
                const validation = validateImageFile(file);
                if (!validation.valid) {
                    window.alert(validation.error);
                    continue;
                }
                try {
                    const uploadResult = await uploadImage(file, {
                        idToken: idToken || undefined,
                        userId: userId || undefined,
                        projectId,
                        useS3: true,
                    });
                    const displayName = uploadResult.name || file.name;
                    const fullPath = resolveS3FullPathForDescription(uploadResult);
                    onAppend(displayName, fullPath);
                } catch (err) {
                    console.error('Description attachment upload failed:', err);
                    window.alert(`Failed to upload ${file.name}`);
                }
            }
        } finally {
            setUploading(false);
            if (inputRef.current) inputRef.current.value = '';
        }
    };

    return (
        <div className="flex items-center gap-2 mt-2">
            <input
                ref={inputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                className="hidden"
                onChange={handleChange}
            />
            <button
                type="button"
                onClick={() => inputRef.current?.click()}
                disabled={uploading}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium
                    bg-white/10 border border-white/20 text-gray-200 hover:bg-white/15 hover:text-white
                    disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title={label}
            >
                {uploading ? (
                    <span className="inline-block h-3.5 w-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                ) : (
                    <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                    </svg>
                )}
                {uploading ? 'Uploading…' : 'Upload attachment'}
            </button>
            <span className="text-[11px] text-gray-500">
                Appends a blank line then{' '}
                <span className="font-mono text-gray-400">
                    Attachement : name ( S3 Full Path : url )
                </span>
            </span>
        </div>
    );
};

export default DescriptionAttachmentUpload;
